import { useId, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsApi } from '../../api/contacts-api';
import { ApiError, ConflictError, ForbiddenError, NotFoundError } from '../../api/errors';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  buildContactInput,
  buildContactPatch,
  contactDraft,
  contactLanguages,
  contactLevelLabels,
  contactLevels,
  contactName,
  contactPurposeLabels,
  contactPurposes,
  customerRegions,
  hasDraftErrors,
  internalPurposes,
  newScopeKey,
  regionKey,
  validateContactDraft,
  type Contact,
  type ContactDraft,
  type ContactLevel,
  type ContactPurpose,
  type ContactScope,
  type ContactScopeDraft,
} from '../../domain/contact';
import type { Site } from '../../domain/inventory';
import { siteLabel, siteSearchAliases } from '../../lib/site-label';

interface ContactDrawerProps {
  customerId: string;
  customerName: string;
  /** The customer's sites, for site and region scopes. */
  sites: readonly Site[];
  /** Edit this contact; omit to add one. */
  contact?: Contact | undefined;
  /** For a new contact, where it applies to start with. */
  initialScope?: ContactScope | undefined;
  onClose: () => void;
}

function saveErrorMessage(error: unknown): string {
  if (error instanceof ConflictError) {
    return 'Someone else changed this contact while you were editing. Close this form and open it again to see their changes.';
  }
  if (error instanceof ForbiddenError) return 'Only Tidal staff can change contacts.';
  if (error instanceof NotFoundError)
    return 'This contact no longer exists. Close this form and reload the page.';
  if (error instanceof ApiError) return error.message;
  return 'The contact could not be saved. Check the fields and try again.';
}

/**
 * Adds or edits a contact: who they are, and where and for what they're the
 * person to talk to (a site, a region or the whole customer, each with its
 * purposes). Saves send the version the form was loaded with.
 */
export function ContactDrawer({
  customerId,
  customerName,
  sites,
  contact,
  initialScope,
  onClose,
}: ContactDrawerProps) {
  const queryClient = useQueryClient();
  const formId = useId();
  const [draft, setDraft] = useState<ContactDraft>(() => contactDraft(contact, initialScope));
  const [showErrors, setShowErrors] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const errors = validateContactDraft(draft);
  const regions = useMemo(() => customerRegions(sites), [sites]);
  const siteOptions = useMemo(
    () =>
      [...sites]
        .sort((a, b) => siteLabel(a).localeCompare(siteLabel(b)))
        .map((site) => ({
          value: site.id,
          label: siteLabel(site),
          aliases: siteSearchAliases(site),
        })),
    [sites],
  );
  const purposes = contactPurposes.filter(
    (purpose) => draft.staff || !internalPurposes.includes(purpose),
  );

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['contacts'] });
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!contact) return contactsApi.createContact(buildContactInput(draft, customerId));
      const patch = buildContactPatch(contact, draft);
      return patch ? contactsApi.updateContact(patch) : contact;
    },
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (error) => setFormError(saveErrorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (contact) await contactsApi.deleteContact(contact.id);
    },
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (error) => {
      setConfirmDelete(false);
      setFormError(saveErrorMessage(error));
    },
  });

  function update<K extends keyof ContactDraft>(field: K, value: ContactDraft[K]): void {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function updateScope(key: string, changes: Partial<ContactScopeDraft>): void {
    setDraft((current) => ({
      ...current,
      scopes: current.scopes.map((scope) => (scope.key === key ? { ...scope, ...changes } : scope)),
    }));
  }

  function togglePurpose(scope: ContactScopeDraft, purpose: ContactPurpose): void {
    updateScope(scope.key, {
      purposes: scope.purposes.includes(purpose)
        ? scope.purposes.filter((candidate) => candidate !== purpose)
        : contactPurposes.filter((p) => p === purpose || scope.purposes.includes(p)),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    if (hasDraftErrors(errors)) {
      setShowErrors(true);
      return;
    }
    save.mutate();
  }

  const busy = save.isPending || remove.isPending;
  const title = contact ? contactName(contact) || 'Contact' : 'New contact';
  const fieldError = (message: string | undefined, id: string) =>
    showErrors && message ? (
      <span className="field-error" id={id}>
        {message}
      </span>
    ) : null;

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer contact-drawer"
        aria-label={contact ? `Edit contact ${title}` : 'New contact'}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="drawer-heading">
          <div>
            <span className="step-label">
              {customerName}
              {contact ? ` · version ${contact.version}` : ''}
            </span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}

        <fieldset className="drawer-section">
          <legend>Person</legend>
          <div className="form-row">
            <label>
              First name
              <input
                type="text"
                autoComplete="off"
                autoFocus={!contact}
                value={draft.firstName}
                onChange={(event) => update('firstName', event.target.value)}
                aria-invalid={showErrors && Boolean(errors.name)}
                aria-describedby={`${formId}-name`}
              />
            </label>
            <label>
              Last name
              <input
                type="text"
                autoComplete="off"
                value={draft.lastName}
                onChange={(event) => update('lastName', event.target.value)}
                aria-invalid={showErrors && Boolean(errors.name)}
                aria-describedby={`${formId}-name`}
              />
            </label>
          </div>
          {fieldError(errors.name, `${formId}-name`)}
          <div className="form-row">
            <label>
              Title
              <input
                type="text"
                autoComplete="off"
                value={draft.title}
                onChange={(event) => update('title', event.target.value)}
              />
            </label>
            <label>
              Role
              <input
                type="text"
                autoComplete="off"
                placeholder="e.g. Site manager"
                value={draft.role}
                onChange={(event) => update('role', event.target.value)}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Email
              <input
                type="email"
                autoComplete="off"
                value={draft.email}
                onChange={(event) => update('email', event.target.value)}
                aria-invalid={showErrors && Boolean(errors.email)}
                aria-describedby={`${formId}-email`}
              />
              {fieldError(errors.email, `${formId}-email`)}
            </label>
            <label>
              Phone
              <input
                type="tel"
                autoComplete="off"
                value={draft.phone}
                onChange={(event) => update('phone', event.target.value)}
              />
            </label>
          </div>
          <label>
            Language for messages
            <select
              aria-label="Language for messages"
              value={draft.language}
              onChange={(event) => update('language', event.target.value)}
            >
              <option value="">{contact ? 'Not set' : 'From the site’s country'}</option>
              {contactLanguages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.label}
                </option>
              ))}
              {draft.language &&
              !contactLanguages.some((language) => language.code === draft.language) ? (
                <option value={draft.language}>{draft.language}</option>
              ) : null}
            </select>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={draft.staff}
              onChange={(event) => update('staff', event.target.checked)}
            />
            Tidal staff (e.g. this customer’s Customer Success owner)
          </label>
          {draft.staff ? (
            <label>
              Tidal email
              <input
                type="email"
                autoComplete="off"
                value={draft.userId}
                onChange={(event) => update('userId', event.target.value)}
                aria-invalid={showErrors && Boolean(errors.userId)}
                aria-describedby={`${formId}-user`}
              />
              {fieldError(errors.userId, `${formId}-user`)}
            </label>
          ) : null}
        </fieldset>

        <fieldset className="drawer-section">
          <legend>Where and for what</legend>
          {draft.scopes.length === 0 ? (
            <p className="drawer-version-note">
              Not attached yet: add a site, a region or the whole customer so this contact is found
              for shipments and notices.
            </p>
          ) : null}
          {draft.scopes.map((scope, index) => {
            const scopeError = errors.scopes?.[scope.key];
            return (
              <div
                className="contact-scope"
                key={scope.key}
                role="group"
                aria-label={`Where ${index + 1}`}
              >
                <div className="contact-scope-where">
                  <label>
                    Applies to
                    <select
                      aria-label="Applies to"
                      value={scope.level}
                      onChange={(event) =>
                        updateScope(scope.key, { level: event.target.value as ContactLevel })
                      }
                    >
                      {contactLevels.map((level) => (
                        <option key={level} value={level}>
                          {level === 'CUSTOMER' ? 'Whole customer' : contactLevelLabels[level]}
                        </option>
                      ))}
                    </select>
                  </label>
                  {scope.level === 'SITE' ? (
                    <label>
                      Site
                      <SearchableSelect
                        ariaLabel={`Site for where ${index + 1}`}
                        value={scope.siteId}
                        onChange={(value) => updateScope(scope.key, { siteId: value })}
                        options={siteOptions}
                        placeholder="Choose a site"
                      />
                    </label>
                  ) : null}
                  {scope.level === 'REGION' ? (
                    <label>
                      Region
                      <select
                        aria-label="Region"
                        value={scope.regionKey}
                        onChange={(event) =>
                          updateScope(scope.key, { regionKey: event.target.value })
                        }
                      >
                        <option value="">Choose a region</option>
                        {regions.map((region) => {
                          const key = regionKey(region.country, region.region);
                          return (
                            <option key={key} value={key}>
                              {region.countryName} / {region.region}
                            </option>
                          );
                        })}
                        {scope.regionKey &&
                        !regions.some(
                          (region) => regionKey(region.country, region.region) === scope.regionKey,
                        ) ? (
                          <option value={scope.regionKey}>{scope.regionKey}</option>
                        ) : null}
                      </select>
                    </label>
                  ) : null}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() =>
                      setDraft((current) => ({
                        ...current,
                        scopes: current.scopes.filter((candidate) => candidate.key !== scope.key),
                      }))
                    }
                  >
                    Remove
                    <span className="visually-hidden"> where {index + 1}</span>
                  </button>
                </div>
                <div
                  className="purpose-options"
                  role="group"
                  aria-label={`Purposes for where ${index + 1}`}
                >
                  {purposes.map((purpose) => (
                    <label key={purpose} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={scope.purposes.includes(purpose)}
                        onChange={() => togglePurpose(scope, purpose)}
                      />
                      {contactPurposeLabels[purpose]}
                    </label>
                  ))}
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={scope.primary}
                    onChange={(event) => updateScope(scope.key, { primary: event.target.checked })}
                  />
                  Primary contact here for these purposes
                </label>
                {showErrors && scopeError ? (
                  <span className="field-error">{scopeError}</span>
                ) : null}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() =>
              setDraft((current) => ({
                ...current,
                scopes: [
                  ...current.scopes,
                  {
                    key: newScopeKey(),
                    level: 'SITE',
                    siteId: '',
                    regionKey: '',
                    purposes: current.staff ? ['CUSTOMER_SUCCESS'] : ['OPERATIONS'],
                    primary: false,
                  },
                ],
              }))
            }
          >
            Add where
          </button>
        </fieldset>

        <fieldset className="drawer-section">
          <legend>Notes</legend>
          <label>
            Notes
            <textarea
              rows={3}
              value={draft.notes}
              onChange={(event) => update('notes', event.target.value)}
            />
          </label>
          {contact ? (
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={draft.inactive}
                onChange={(event) => update('inactive', event.target.checked)}
              />
              No longer a contact (kept for history, never chosen)
            </label>
          ) : null}
        </fieldset>

        <div className="drawer-actions">
          {contact ? (
            confirmDelete ? (
              <button
                type="button"
                className="button-danger"
                onClick={() => remove.mutate()}
                disabled={busy}
              >
                {remove.isPending ? 'Deleting…' : 'Delete their details for good'}
              </button>
            ) : (
              <button
                type="button"
                className="link-button"
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
              >
                Delete contact
              </button>
            )
          ) : null}
          <span className="contact-actions-spacer" />
          <button type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy}>
            {save.isPending ? 'Saving…' : contact ? 'Save changes' : 'Add contact'}
          </button>
        </div>
      </form>
    </div>
  );
}
